/**
 * Autenticação de verdade: GoTrue + o vínculo do usuário com a clínica.
 *
 * O login diz QUEM é a pessoa. Em que clínica ela está, com que papel e o que
 * alcança sai do banco — da tabela `membros`, sob RLS, que só devolve a linha
 * dela. O cliente não escolhe o próprio tenant; ele descobre qual é o seu.
 */

import { supabase } from '@/supabase';
import { clinicaPreferida, escolherVinculo } from './clinicaAtiva';
import { PAPEIS } from './papeis';
import { mensagemSemAcesso } from './semAcesso';
import type { SituacaoDoVinculo } from './semAcesso';
import type {
    CadastroDeTotp, EstadoDoMfa, Impersonacao, Papel, PortaDeAutenticacao, ResultadoDeCadastro, ResultadoLogin, Sessao,
} from './tipos';

interface LinhaDeMembro {
    id: string;
    clinica_id: string;
    nome: string;
    papel: Papel;
    permissoes: string[];
    clinicas: { nome: string } | null;
}

const ERROS: Record<string, string> = {
    'Invalid login credentials': 'E-mail ou senha incorretos.',
    'Email not confirmed': 'Confirme o e-mail antes de entrar.',
};

/**
 * A sessão de quem opera a plataforma, com as permissões GRAVADAS para ele.
 *
 * Antes era sempre o pacote inteiro do super-admin: o que o banco registrava
 * para cada operador não mudava nada. Vazio é vazio — operador sem permissão
 * gravada não ganha o pacote por omissão.
 */
export function sessaoDoOperador(
    operador: { nome: string | null; permissoes: string[] | null },
    email: string,
    nomeDoMetadado: string | undefined,
): Sessao {
    return {
        email,
        nome: operador.nome ?? nomeDoMetadado ?? email,
        papel: 'super-admin',
        perfil: 'plataforma',
        permissoes: [...new Set((operador.permissoes ?? []).map(String))],
        clinica: null,
        clinicaId: null,
        clinicas: [],
        /* Operador da plataforma não tem clínica: módulo desligado é
           pergunta que só existe dentro de uma. */
        modulosDesligados: [],
        entrouEm: new Date().toISOString(),
    };
}

async function montarSessao(usuarioId: string, email: string, nomeDoMetadado: string | undefined): Promise<Sessao | null> {
    if (!supabase) return null;

    const [{ data: membros }, { data: operador }] = await Promise.all([
        supabase
            .from('membros')
            .select('id, clinica_id, nome, papel, permissoes, clinicas(nome)')
            /* Filtrar por `usuario_id` é obrigatório, não redundante: a RLS
               deixa o membro ler a EQUIPE inteira da clínica — é dela que sai a
               aba de profissionais. Sem o filtro, a sessão nascia com o nome e o
               papel de quem estivesse na primeira linha. */
            .eq('usuario_id', usuarioId)
            .eq('acesso', 'ativo')
            .returns<LinhaDeMembro[]>(),
        /* Pela linha DELE: com dois operadores cadastrados, `maybeSingle` sem
           filtro dependeria só da RLS para não estourar ou pegar o outro. */
        supabase.from('operadores_da_plataforma').select('nome, permissoes').eq('usuario_id', usuarioId).maybeSingle(),
    ]);

    if (operador) return sessaoDoOperador(operador, email, nomeDoMetadado);

    /* A sessão nasce na clínica escolhida por último, se ainda for do vínculo;
       senão na primeira por nome. Era a primeira linha que o banco devolvesse —
       ordem que ninguém garante. */
    const vinculos = (membros ?? []).map((m) => ({ clinicaId: m.clinica_id, clinica: m.clinicas?.nome ?? '—', membro: m }));
    const primeiro = escolherVinculo(vinculos, clinicaPreferida())?.membro;
    /* Login válido sem vínculo é conta criada e ainda não convidada para clínica
       nenhuma. Deixar entrar numa tela vazia seria pior que dizer o motivo. */
    if (!primeiro) return null;

    const doPapel = PAPEIS[primeiro.papel] ?? PAPEIS.profissional;
    return {
        email,
        nome: primeiro.nome,
        papel: primeiro.papel,
        perfil: doPapel.perfil,
        /* As do papel mais as concedidas à pessoa. */
        permissoes: [...new Set([...doPapel.permissoes, ...(primeiro.permissoes ?? [])])],
        clinica: primeiro.clinicas?.nome ?? null,
        clinicaId: primeiro.clinica_id,
        /* Uma pessoa pode ser membro de mais de uma clínica da rede. */
        clinicas: [...new Set((membros ?? []).map((m) => m.clinicas?.nome).filter((n): n is string => !!n))],
        modulosDesligados: await modulosDesligados(primeiro.clinica_id),
        entrouEm: new Date().toISOString(),
    };
}

/** O Postgres devolve "…: mensagem"; a tela quer só a mensagem. */
function limparErro(mensagem: string): string {
    return mensagem.replace(/^.*?:\s*/, '') || mensagem;
}

/** Os módulos que esta clínica não tem. A resolução padrão/exceção é do banco. */
async function modulosDesligados(clinicaId: string): Promise<string[]> {
    if (!supabase) return [];
    const { data } = await supabase.rpc('modulos_desligados', { p_clinica: clinicaId });
    return ((data ?? []) as unknown as string[]).map(String);
}

/** A impersonação aberta do próprio operador, se houver. */
async function impersonacaoViva(): Promise<Impersonacao | null> {
    if (!supabase) return null;
    const { data } = await supabase.rpc('minha_impersonacao');
    const linha = (data as unknown as Record<string, unknown>[] | null)?.[0];
    if (!linha) return null;
    return {
        id: String(linha.id),
        clinicaId: String(linha.clinica_id),
        clinica: String(linha.clinica ?? ''),
        motivo: String(linha.motivo ?? ''),
        readOnly: true,
        inicio: new Date(String(linha.iniciada_em)).getTime(),
        expiraEm: new Date(String(linha.expira_em)).getTime(),
    };
}

/**
 * O recorte que a impersonação impõe à sessão.
 *
 * O tenant passa a ser o da clínica visitada — é ele que os adaptadores usam —,
 * e o perfil continua sendo o de plataforma: quem entrou não virou membro.
 */
function comImpersonacao(imp: Impersonacao): Partial<Sessao> {
    return { clinica: imp.clinica, clinicaId: imp.clinicaId, impersonando: imp };
}

/** Dentro de uma impersonação, o menu é o da clínica visitada. */
async function modulosDaImpersonacao(imp: Impersonacao): Promise<string[]> {
    return modulosDesligados(imp.clinicaId);
}

export const autenticacaoSupabase: PortaDeAutenticacao = {
    async entrar(email: string, senha: string): Promise<ResultadoLogin> {
        if (!supabase) return { ok: false, erro: 'Servidor não configurado.' };
        const e = String(email ?? '').trim().toLowerCase();
        if (!e || !senha) return { ok: false, erro: 'Informe e-mail e senha para continuar.' };

        const { data, error } = await supabase.auth.signInWithPassword({ email: e, password: senha });
        if (error) return { ok: false, erro: ERROS[error.message] ?? error.message };

        const sessao = data.user
            ? await montarSessao(data.user.id, data.user.email ?? e, data.user.user_metadata?.nome as string | undefined)
            : null;
        if (!sessao) {
            /* Antes de sair: com o token ainda válido, o banco conta se o vínculo
               existe e só a clínica está suspensa (0096). */
            const { data: vinculos } = await supabase.rpc('situacao_dos_meus_vinculos');
            await supabase.auth.signOut();
            return { ok: false, erro: mensagemSemAcesso((vinculos ?? []) as SituacaoDoVinculo[]) };
        }
        return { ok: true, sessao };
    },

    async sair(): Promise<void> {
        await supabase?.auth.signOut();
    },

    async recuperar(): Promise<Sessao | null> {
        if (!supabase) return null;
        const { data } = await supabase.auth.getSession();
        const usuario = data.session?.user;
        if (!usuario?.email) return null;

        const sessao = await montarSessao(usuario.id, usuario.email, usuario.user_metadata?.nome as string | undefined);
        if (!sessao) return null;

        /* Recarregar a página no meio de uma impersonação não pode devolver a
           pessoa ao painel como se nada estivesse aberto: a impersonação vive
           no banco, com prazo, e é de lá que ela volta. */
        const viva = await impersonacaoViva();
        if (!viva) return sessao;
        return {
            ...sessao,
            ...comImpersonacao(viva),
            modulosDesligados: await modulosDaImpersonacao(viva),
        };
    },

    async criarConta({ nome, email, senha, destino }): Promise<ResultadoDeCadastro> {
        if (!supabase) return { ok: false, erro: 'Servidor não configurado.' };
        const e = String(email ?? '').trim().toLowerCase();
        if (!e.includes('@')) return { ok: false, erro: 'Informe um e-mail válido.' };
        if (senha.length < 8) return { ok: false, erro: 'A senha precisa de pelo menos 8 caracteres.' };

        /* O nome, quando vem, fica no metadado do usuário: na hora do cadastro a
           conta ainda não tem linha em `membros` para guardá-lo. O link do e-mail
           volta para `destino` — é lá que quem não foi convidado abre a clínica. */
        const limpo = String(nome ?? '').trim();
        const { data, error } = await supabase.auth.signUp({
            email: e,
            password: senha,
            options: { ...(limpo ? { data: { nome: limpo } } : {}), ...(destino ? { emailRedirectTo: destino } : {}) },
        });
        if (error) return { ok: false, erro: ERROS[error.message] ?? error.message };

        /* Sessão já aberta significa confirmação de e-mail desligada. Sem
           sessão, a conta existe e espera o clique no e-mail. */
        return { ok: true, precisaConfirmar: !data.session };
    },

    /* O botão só aparece com o Google ligado no servidor: sem isso, o clique levaria a
       pessoa a uma página de erro em JSON do GoTrue. */
    async googleDisponivel(): Promise<boolean> {
        const url = import.meta.env.VITE_SUPABASE_URL;
        const chave = import.meta.env.VITE_SUPABASE_ANON_KEY;
        if (!supabase || !url || !chave) return false;
        try {
            const r = await fetch(`${url}/auth/v1/settings`, { headers: { apikey: chave } });
            if (!r.ok) return false;
            const s = await r.json() as { external?: { google?: boolean } };
            return s.external?.google === true;
        } catch {
            return false;
        }
    },

    async entrarComGoogle(destino: string): Promise<{ ok: boolean; erro?: string }> {
        if (!supabase) return { ok: false, erro: 'Servidor não configurado.' };
        const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: destino } });
        if (error) return { ok: false, erro: error.message };
        return { ok: true };
    },

    async contaSemClinica() {
        if (!supabase) return null;
        const { data } = await supabase.auth.getSession();
        const usuario = data.session?.user;
        if (!usuario?.email) return null;
        /* Tem sessão montável (é membro ativo ou opera a plataforma): não é conta sem clínica. */
        if (await montarSessao(usuario.id, usuario.email, usuario.user_metadata?.nome as string | undefined)) return null;
        const meta = (usuario.user_metadata ?? {}) as Record<string, unknown>;
        /* O Google manda `full_name` e `name`; o cadastro com senha, `nome`. */
        const nome = [meta.nome, meta.full_name, meta.name].find((v): v is string => typeof v === 'string' && v.trim().length > 0);
        return { email: usuario.email, nome: nome?.trim() ?? null, emailConfirmado: Boolean(usuario.email_confirmed_at) };
    },

    async pedirRecuperacao(email: string, destino: string): Promise<{ ok: boolean; erro?: string }> {
        if (!supabase) return { ok: false, erro: 'Servidor não configurado.' };
        const e = String(email ?? '').trim().toLowerCase();
        if (!e.includes('@')) return { ok: false, erro: 'Informe um e-mail válido.' };

        const { error } = await supabase.auth.resetPasswordForEmail(e, { redirectTo: destino });
        /* Erro de rede é erro; conta inexistente NÃO é — e a tela responde a
           mesma coisa nos dois casos, para não virar verificador de cadastro. */
        if (error && error.status !== 400) return { ok: false, erro: error.message };
        return { ok: true };
    },

    async iniciarImpersonacao(clinicaId: string, motivo: string): Promise<Impersonacao> {
        if (!supabase) throw new Error('Servidor não configurado.');
        const { error } = await supabase.rpc('iniciar_impersonacao', { p_clinica: clinicaId, p_motivo: motivo });
        if (error) throw new Error(limparErro(error.message));

        const viva = await impersonacaoViva();
        if (!viva) throw new Error('A impersonação não abriu. Tente de novo.');
        return viva;
    },

    async encerrarImpersonacao(): Promise<void> {
        if (!supabase) return;
        const { error } = await supabase.rpc('encerrar_impersonacao');
        if (error) throw new Error(limparErro(error.message));
    },

    async definirNovaSenha(senha: string): Promise<{ ok: boolean; erro?: string }> {
        if (!supabase) return { ok: false, erro: 'Servidor não configurado.' };
        if (senha.length < 8) return { ok: false, erro: 'A senha precisa de pelo menos 8 caracteres.' };
        const { error } = await supabase.auth.updateUser({ password: senha });
        if (error) return { ok: false, erro: ERROS[error.message] ?? error.message };
        return { ok: true };
    },

    async estadoDoMfa(): Promise<EstadoDoMfa> {
        if (!supabase) return { disponivel: false, motivo: 'Servidor não configurado.' };
        const { data, error } = await supabase.auth.mfa.listFactors();
        if (error) {
            return { disponivel: false, motivo: `O servidor de autenticação não respondeu sobre o MFA: ${error.message}` };
        }
        return {
            disponivel: true,
            fatores: (data?.all ?? []).map((f) => ({
                id: f.id,
                tipo: String(f.factor_type),
                ...(f.friendly_name ? { nome: f.friendly_name } : {}),
                verificado: f.status === 'verified',
            })),
        };
    },

    async cadastrarTotp(): Promise<CadastroDeTotp> {
        if (!supabase) throw new Error('Servidor não configurado.');
        const { data, error } = await supabase.auth.mfa.enroll({
            factorType: 'totp',
            /* O nome distingue dois aparelhos da mesma pessoa na lista de fatores. */
            friendlyName: `Fórmula MED ${new Date().toISOString().replace('T', ' ').slice(0, 16)}`,
        });
        /* Recusa do servidor (MFA desligado na configuração, por exemplo) sobe com
           a mensagem dele: a tela diz por que não deu, em vez de um QR que não vale. */
        if (error) throw new Error(error.message);
        const totp = (data as { id?: string; totp?: { qr_code?: string; secret?: string } } | null);
        if (!totp?.id || !totp.totp?.qr_code || !totp.totp.secret) {
            throw new Error('O servidor de autenticação não devolveu o QR do autenticador.');
        }
        return { fatorId: totp.id, qrCode: totp.totp.qr_code, segredo: totp.totp.secret };
    },

    async confirmarTotp(fatorId: string, codigo: string): Promise<void> {
        if (!supabase) throw new Error('Servidor não configurado.');
        const limpo = codigo.replace(/\D/g, '');
        if (limpo.length !== 6) throw new Error('O código do autenticador tem seis dígitos.');
        const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: fatorId, code: limpo });
        if (error) {
            throw new Error(/invalid/i.test(error.message)
                ? 'Código incorreto ou vencido. Digite o que o aplicativo mostra agora.'
                : error.message);
        }
    },

    async descartarFator(fatorId: string): Promise<void> {
        if (!supabase) throw new Error('Servidor não configurado.');
        const { error } = await supabase.auth.mfa.unenroll({ factorId: fatorId });
        if (error) throw new Error(error.message);
    },
};
